// Waiting on a background job the server is running for us.
//
// Three of these exist now — price generation, the admin console's forced
// recalculation, and the property analysis — and each has to survive the same
// two failure modes, both seen in practice: a job that never leaves `running`,
// and a status request that blips. The first is capped by a deadline; the
// second is retried, because abandoning a five-minute wait over one dropped
// poll throws away a job that is still perfectly healthy.
//
// That is one problem, so it is solved once here. What differs between the
// callers is only what a job looks like, when it counts as finished, and what
// to throw when it never does — so those are the parameters.

/** Consecutive status failures tolerated before giving up (~6s of blips). */
const MAX_CONSECUTIVE_FAILURES = 3;
const POLL_INTERVAL_MS = 2_000;

export interface JobPollOptions<J> {
    /** Read the job's current state. `null` means "no such job right now". */
    fetchStatus: () => Promise<J | null>;
    /** Has it finished — either way? A settled job is returned, not thrown. */
    isSettled: (job: J) => boolean;
    /** Called on each successful poll, so a long run can show real progress. */
    onProgress?: (job: J) => void;
    /**
     * Called once when the job passes {@link slowAfterMs} without finishing, so
     * the UI can say "still working" instead of showing a spinner that is
     * indistinguishable from a hang.
     */
    onSlow?: () => void;
    /** How long before the job counts as slow. Default 20s. */
    slowAfterMs?: number;
    /** Hard client deadline. Default 5 minutes. */
    maxWaitMs?: number;
    /** What to throw at the deadline — the caller names its own job. */
    onTimeout: (waitedMs: number) => Error;
    /** What to throw when the job has vanished for three polls running. */
    onVanished?: () => Error;
    /**
     * Stops the wait: no further request is made and the promise rejects with
     * an `AbortError`. For a caller whose wait a newer one has taken over —
     * the job it was watching is no longer the one that matters.
     */
    signal?: AbortSignal;
}

/** The error a wait rejects with when its `signal` fires. */
export function isAbortError(err: unknown): boolean {
    return typeof err === "object" && err !== null && (err as { name?: unknown }).name === "AbortError";
}

function abortError(signal: AbortSignal): Error {
    return signal.reason instanceof Error && isAbortError(signal.reason)
        ? signal.reason
        : new DOMException("The wait was aborted", "AbortError");
}

/** The pause between polls, cut short by an abort rather than waited out. */
function pause(ms: number, signal?: AbortSignal): Promise<void> {
    return new Promise((resolve, reject) => {
        if (signal?.aborted) {
            reject(abortError(signal));
            return;
        }
        const onAbort = () => {
            clearTimeout(timer);
            reject(abortError(signal as AbortSignal));
        };
        const timer = setTimeout(() => {
            signal?.removeEventListener("abort", onAbort);
            resolve();
        }, ms);
        signal?.addEventListener("abort", onAbort, { once: true });
    });
}

/**
 * Poll a job until it settles, and hand back the settled job.
 *
 * Throws only when it cannot answer: the deadline passed, the status request
 * failed three times running, or the job disappeared for three polls. A job
 * that finished badly is a normal return — its own state says so, and the
 * caller has the words for it.
 */
export async function waitForJobToSettle<J>(options: JobPollOptions<J>): Promise<J> {
    const maxWaitMs = options.maxWaitMs ?? 5 * 60 * 1_000;
    const slowAfterMs = options.slowAfterMs ?? 20_000;

    const start = Date.now();
    let consecutiveFailures = 0;
    let announcedSlow = false;
    const { signal } = options;

    while (Date.now() - start < maxWaitMs) {
        await pause(POLL_INTERVAL_MS, signal);

        if (!announcedSlow && Date.now() - start >= slowAfterMs) {
            announcedSlow = true;
            options.onSlow?.();
        }

        let job: J | null;
        try {
            job = await options.fetchStatus();
            consecutiveFailures = 0;
        } catch (err) {
            if (signal?.aborted) throw abortError(signal);
            if (++consecutiveFailures >= MAX_CONSECUTIVE_FAILURES) throw err;
            continue;
        }
        // Aborted while the request was out: its answer belongs to a wait
        // nobody is running any more.
        if (signal?.aborted) throw abortError(signal);

        // A job can be evicted between polls on a slow client; treat a single
        // disappearance the same way as a blip rather than a hard failure.
        if (job === null || job === undefined) {
            if (++consecutiveFailures >= MAX_CONSECUTIVE_FAILURES) {
                throw options.onVanished?.() ?? new Error("The job disappeared");
            }
            continue;
        }

        options.onProgress?.(job);

        if (options.isSettled(job)) return job;
    }

    throw options.onTimeout(Date.now() - start);
}
